import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Download } from 'lucide-react';
import { toast } from 'sonner';

import { fbmCatalogApi } from 'api/FBM/fbmCatalog';
import { toCatalogApiFilters } from 'components/fbm/components/partnerCatalog/PartnerCatalogFilterConfig';
import { PartnerCatalogFilterPills } from 'components/fbm/components/partnerCatalog/PartnerCatalogFilterPills';
import { PartnerCatalogFilters } from 'components/fbm/components/partnerCatalog/PartnerCatalogFilters';
import { PartnerCatalogTable } from 'components/fbm/components/partnerCatalog/PartnerCatalogTable';
import {
    CATALOG_PAGE_SIZE_OPTIONS,
    DEFAULT_CATALOG_FILTER,
    DEFAULT_CATALOG_PAGE,
    DEFAULT_CATALOG_PAGE_SIZE,
    DEFAULT_CATALOG_SORT_DIR,
    DEFAULT_CATALOG_SORT_FIELD,
    PartnerCatalogSortConfig,
} from 'components/fbm/components/partnerCatalog/partnerCatalogUtils';
import { UserContext } from 'contexts/UserContext';
import { Button } from 'shadcn/components/ui/Button';
import { useFbmCatalogQuery } from 'shared/hooks/fbm/useFbmCatalogQuery';

function ViewPartnerCatalog() {
    const { fbmAccount } = useContext(UserContext);
    const fbmAccountName = fbmAccount?.fbmAccountName;

    const [filter, setFilter] = useState(DEFAULT_CATALOG_FILTER);
    const [page, setPage] = useState(DEFAULT_CATALOG_PAGE);
    const [pageSize, setPageSize] = useState(DEFAULT_CATALOG_PAGE_SIZE);
    const [sortField, setSortField] = useState(DEFAULT_CATALOG_SORT_FIELD);
    const [sortDir, setSortDir] = useState(DEFAULT_CATALOG_SORT_DIR);
    const [uiFilters, setUiFilters] = useState({});
    const [isExporting, setIsExporting] = useState(false);

    const apiFilters = useMemo(() => toCatalogApiFilters(uiFilters), [uiFilters]);

    // Reset filter/search/sort state when switching FBM accounts.
    const previousAccountNameRef = useRef(fbmAccountName);
    if (previousAccountNameRef.current !== fbmAccountName) {
        previousAccountNameRef.current = fbmAccountName;
        setFilter(DEFAULT_CATALOG_FILTER);
        setPage(DEFAULT_CATALOG_PAGE);
        setPageSize(DEFAULT_CATALOG_PAGE_SIZE);
        setSortField(DEFAULT_CATALOG_SORT_FIELD);
        setSortDir(DEFAULT_CATALOG_SORT_DIR);
        setUiFilters({});
    }

    // Reset page during render when filter/search/sort change so the next query uses page 1.
    const catalogSearchKey = `${filter}|${JSON.stringify(apiFilters)}|${sortField}|${sortDir}`;
    const [prevCatalogSearchKey, setPrevCatalogSearchKey] = useState(catalogSearchKey);
    const searchChanged = catalogSearchKey !== prevCatalogSearchKey;
    if (searchChanged) {
        setPrevCatalogSearchKey(catalogSearchKey);
        setPage(DEFAULT_CATALOG_PAGE);
    }
    const pageForQuery = searchChanged ? DEFAULT_CATALOG_PAGE : page;

    const { data, error, isFetching, isLoading } = useFbmCatalogQuery({
        enabled: !!fbmAccount?.id,
        fbmAccountName,
        filter,
        page: pageForQuery,
        pageSize,
        sortDir,
        sortField,
        ...apiFilters,
    });

    const rows = useMemo(() => data?.rows ?? [], [data]);
    const total = data?.total ?? 0;
    const totalPages = data?.totalPages ?? 0;
    const currentPage = pageForQuery;

    const sorting = useMemo(() => {
        const columnId = Object.entries(PartnerCatalogSortConfig.sortableFields).find(
            ([, cfg]) => cfg.apiField === sortField,
        )?.[0];
        if (!columnId) return [];
        return [{ id: columnId, desc: sortDir === 'desc' }];
    }, [sortField, sortDir]);

    const handleSortColumn = useCallback(
        (columnId) => {
            const apiField = PartnerCatalogSortConfig.sortableFields[columnId]?.apiField;
            if (!apiField) return;

            if (apiField === sortField) {
                setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'));
                return;
            }

            setSortField(apiField);
            setSortDir(DEFAULT_CATALOG_SORT_DIR);
        },
        [sortField],
    );

    useEffect(() => {
        if (error) {
            toast.error(`Failed to load catalog: ${error.message}`);
        }
    }, [error]);

    const handleFilterChange = (nextFilter) => {
        setFilter(nextFilter);
    };

    const handleSearchFiltersChange = (nextFilters) => {
        setUiFilters(nextFilters ?? {});
    };

    const handlePageSizeChange = (nextPageSize) => {
        setPageSize(nextPageSize);
        setPage(DEFAULT_CATALOG_PAGE);
    };

    const handleExportCsv = async () => {
        if (!fbmAccountName || isExporting) return;
        setIsExporting(true);
        try {
            const response = await fbmCatalogApi.exportCatalog({
                filter,
                fbmAccount: fbmAccountName,
                sortDir,
                sortField,
                ...apiFilters,
            });
            if (!response?.status) {
                toast.error(`Failed to export catalog: ${response?.msg || 'Unknown error'}`);
            }
        } catch (exportError) {
            toast.error(`Failed to export catalog: ${exportError.message || 'Unknown error'}`);
        } finally {
            setIsExporting(false);
        }
    };

    const title = fbmAccount?.companyName ? `${fbmAccount.companyName} - Catalog` : 'Catalog';

    if (!fbmAccountName) {
        return (
            <div className="mt-4">
                <h2 className="mb-4 text-lg font-semibold">{title}</h2>
                <p className="text-sm text-muted-foreground">Please select an FBM account above to view the catalog.</p>
            </div>
        );
    }

    return (
        <div className="mt-4 flex flex-col gap-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-lg font-semibold">{title}</h2>
                <div className="flex flex-wrap items-center gap-2">
                    <PartnerCatalogFilterPills
                        fbmAccountName={fbmAccountName}
                        value={filter}
                        onValueChange={handleFilterChange}
                    />
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isExporting || isLoading}
                        onClick={handleExportCsv}
                    >
                        <Download className="h-4 w-4" />
                        {isExporting ? 'Exporting…' : 'Export CSV'}
                    </Button>
                </div>
            </div>
            <PartnerCatalogFilters initialValues={uiFilters} onChange={handleSearchFiltersChange} />
            <PartnerCatalogTable
                data={rows}
                fbmAccountName={fbmAccountName}
                isLoading={isLoading}
                isFetching={isFetching}
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={total}
                pageSize={pageSize}
                pageSizeOptions={CATALOG_PAGE_SIZE_OPTIONS}
                hasPrevious={currentPage > 1}
                hasNext={totalPages > 0 ? currentPage < totalPages : false}
                onPrevious={() => setPage((current) => Math.max(DEFAULT_CATALOG_PAGE, current - 1))}
                onNext={() => setPage((current) => current + 1)}
                onPageSizeChange={handlePageSizeChange}
                sorting={sorting}
                onSortColumn={handleSortColumn}
            />
        </div>
    );
}

export { ViewPartnerCatalog };
